import type { ReactNode } from 'react';
import { Button, Icon, Tooltip } from '@forecastify/ui';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ A rail row that is edited in a small dialog AND leads somewhere.          │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ux-pass-2, 14 Oct: the cost's and the invoice's details are edited in place, with no *Szerkesztés*
 * button. Most rows are one value — a `RailPicker`, a `RailDate`, a `RailMoney`. Two are not: what a
 * cost is attached to (a kind, then a line item or a contract AND a period) and what an invoice bills
 * (the same shape), and a cost's recurrence (a unit, a step and an end). Those open a dialog that
 * asks only that question, from the row itself.
 *
 * ⚠️ **The arrow is a second, separate target** that opens what the row names — the row used to be
 * that link, and making it an editor must not take the way there away. A reader who may not edit
 * gets the old behaviour: the whole row navigates (`onEdit` absent).
 */
export function RailEditLink({
  value,
  before,
  editLabel,
  openLabel,
  onEdit,
  onOpen,
}: {
  readonly value: string;
  readonly before?: ReactNode;
  /** Names the edit for a screen reader and as the hover tooltip. */
  readonly editLabel: string;
  readonly openLabel: string;
  /** Absent for a reader who may not edit. */
  readonly onEdit?: () => void;
  /** Absent when the row names nothing that has a page. */
  readonly onOpen?: () => void;
}) {
  const box =
    'flex h-[30px] w-full min-w-0 items-center gap-2 rounded-md px-2 text-left text-[12.5px] transition-colors';

  if (onEdit === undefined) {
    return (
      <Tooltip asChild content={openLabel}>
        <button
          type="button"
          disabled={onOpen === undefined}
          className={`${box} hover:bg-accent/60 disabled:hover:bg-transparent`}
          onClick={onOpen}
        >
          {before}
          <span className="min-w-0 flex-1 truncate">{value}</span>
          {onOpen === undefined ? null : (
            <Icon name="arrow-right" size={12} className="text-muted-foreground opacity-60" />
          )}
        </button>
      </Tooltip>
    );
  }

  return (
    <span className="flex min-w-0 items-center gap-0.5">
      <Tooltip asChild content={editLabel}>
        <button
          type="button"
          aria-label={`${editLabel}: ${value}`}
          className={`${box} hover:bg-accent/60`}
          onClick={onEdit}
        >
          {before}
          <span className="min-w-0 flex-1 truncate">{value}</span>
          <Icon name="chevron-down" size={12} className="text-muted-foreground opacity-60" />
        </button>
      </Tooltip>
      {onOpen === undefined ? null : (
        <Button
          variant="ghost"
          size="icon-sm"
          className="text-muted-foreground size-[30px] shrink-0"
          aria-label={openLabel}
          title={openLabel}
          onClick={onOpen}
        >
          <Icon name="arrow-right" size={12} />
        </Button>
      )}
    </span>
  );
}
